/**
 * 3D Room Visualizer — Transparency Controller
 *
 * Camera-driven dynamic opacity for walls. The wall the camera faces
 * (farthest from camera) is opaque; walls between camera and that wall
 * are semi-transparent so you can see through them.
 */

import * as THREE from 'three';

export interface TransparencyState {
  front: number;
  back: number;
  left: number;
  right: number;
  ceiling: number;
}

const MIN_OPACITY = 0.0;     // Fully invisible for near walls
const MAX_OPACITY = 1.0;
const OPAQUE_THRESHOLD = 0.15;
const TRANSPARENT_THRESHOLD = -0.15;

/**
 * Calculate target opacities for all walls based on camera azimuth/elevation.
 *
 * The wall whose inward normal most closely aligns with the camera's
 * look direction (camera-to-center) gets maximum opacity. Walls whose
 * normals point away from the camera get minimum opacity.
 */
export function calculateWallOpacities(
  azimuthRad: number,
  elevationRad: number
): TransparencyState {
  // Camera look direction dot products with each wall's inward normal:
  // Front (normal +Z into room): facing = cos(azimuth)
  // Back  (normal -Z): facing = -cos(azimuth)
  // Left  (normal +X): facing = sin(azimuth)
  // Right (normal -X): facing = -sin(azimuth)
  const facingFront = Math.cos(azimuthRad);
  const facingBack = -Math.cos(azimuthRad);
  const facingLeft = Math.sin(azimuthRad);
  const facingRight = -Math.sin(azimuthRad);

  // Ceiling: transparent when looking down from above, opaque when looking up from below
  // Positive elevation = camera above (looking down) → ceiling transparent
  // Negative elevation = camera below (looking up) → ceiling opaque
  const ceilingFacing = -Math.sin(elevationRad);

  return {
    front: mapToOpacity(facingFront),
    back: mapToOpacity(facingBack),
    left: mapToOpacity(facingLeft),
    right: mapToOpacity(facingRight),
    ceiling: mapToOpacity(ceilingFacing),
  };
}

/**
 * Map a facing value [-1, 1] to an opacity value.
 * Uses smoothstep for a pleasing transition.
 */
function mapToOpacity(facing: number): number {
  if (facing >= OPAQUE_THRESHOLD) return MAX_OPACITY;
  if (facing <= TRANSPARENT_THRESHOLD) return MIN_OPACITY;

  const t = (facing - TRANSPARENT_THRESHOLD) / (OPAQUE_THRESHOLD - TRANSPARENT_THRESHOLD);
  const smooth = t * t * (3 - 2 * t); // smoothstep
  return MIN_OPACITY + (MAX_OPACITY - MIN_OPACITY) * smooth;
}

/**
 * Smoothly interpolate current opacities toward target opacities.
 * Call this each render frame for smooth transitions.
 */
export function lerpOpacities(
  current: TransparencyState,
  target: TransparencyState,
  factor: number = 0.15
): TransparencyState {
  return {
    front: THREE.MathUtils.lerp(current.front, target.front, factor),
    back: THREE.MathUtils.lerp(current.back, target.back, factor),
    left: THREE.MathUtils.lerp(current.left, target.left, factor),
    right: THREE.MathUtils.lerp(current.right, target.right, factor),
    ceiling: THREE.MathUtils.lerp(current.ceiling, target.ceiling, factor),
  };
}

/**
 * Apply opacity values to wall groups by traversing their meshes.
 */
export function applyOpacityToGroup(
  group: THREE.Group,
  opacity: number
): void {
  group.traverse((child) => {
    if (child instanceof THREE.Mesh && child.material) {
      const mat = child.material as THREE.MeshStandardMaterial;
      mat.transparent = true;
      mat.opacity = opacity;
      mat.depthWrite = opacity > 0.9;
      mat.needsUpdate = true;
    }
    if (child instanceof THREE.LineSegments && child.material) {
      const mat = child.material as THREE.LineBasicMaterial;
      mat.transparent = true;
      mat.opacity = Math.min(opacity, 0.7);
      mat.needsUpdate = true;
    }
  });

  // Transparent walls render after opaque ones
  group.renderOrder = opacity > 0.9 ? 0 : 1;
}
